import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const checklistGroups = [
  {
    id: 'before',
    emoji: '👜',
    title: 'Before',
    items: [
      'Eat something real and drink water before you head out.',
      'Pack what you need: meds, lube, barriers, chargers, and safer-use supplies.',
      'Set your limits around drugs, sex, timing, and consent while you feel clear.',
      'Let one trusted person know your rough plan if that feels safe.'
    ]
  },
  {
    id: 'during',
    emoji: '✨',
    title: 'During',
    items: [
      'Start low, go slow, and avoid rushing redoses.',
      'Hydrate, take breaks, and check in with your body every so often.',
      'Do not leave someone behind if they look off, too sleepy, or unwell.',
      'Consent is ongoing — you can pause, stop, or change your mind anytime.'
    ]
  },
  {
    id: 'after',
    emoji: '🌙',
    title: 'After',
    items: [
      'Do aftercare: water, food, sleep, shower, and softer lighting if you need it.',
      'Use RAPS if you feel wrung out: Rest, Aqua, Protein, Sugar.',
      'Ground yourself with slow breathing, something warm, and something real to touch.',
      'Reach out instead of isolating if the comedown feels heavy.'
    ]
  }
];

export default function SaferUseChecklistPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col relative z-10" data-testid="safer-use-checklist-page">
      <header className="glass-morphism sticky top-0 z-50 border-b">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              data-testid="safer-use-checklist-back-button"
              className="mr-4 text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--primary))]"
            >
              <ArrowLeft size={24} weight="duotone" />
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-3xl emoji-bounce">✅</span>
              <h1 className="text-2xl font-bold text-gradient" data-testid="safer-use-checklist-title">
                Safer-Use Checklist
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-8 pb-24">
        <section className="mb-8 rounded-[2rem] border border-[rgb(var(--primary))]/20 bg-gradient-to-br from-[rgb(var(--primary))]/15 to-[rgb(var(--secondary))]/10 p-6" data-testid="safer-use-checklist-hero">
          <div className="text-5xl mb-4">🫶</div>
          <h2 className="text-3xl font-semibold text-[rgb(var(--text-primary))] mb-3">A simple check-in before, during, and after.</h2>
          <p className="text-sm sm:text-base text-[rgb(var(--text-secondary))] leading-relaxed">
            This is a quick home for the basics: prep what you need, stay connected while you party, and make room for aftercare once things slow down.
          </p>
        </section>

        <section className="space-y-4" data-testid="safer-use-checklist-groups">
          {checklistGroups.map((group) => (
            <article
              key={group.id}
              data-testid={`safer-use-checklist-group-${group.id}`}
              className="rounded-[2rem] border border-white/10 bg-[rgb(var(--surface))] p-5"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="text-3xl">{group.emoji}</div>
                <h3 className="text-xl font-semibold text-[rgb(var(--text-primary))]">{group.title}</h3>
              </div>
              <div className="space-y-3">
                {group.items.map((item, index) => (
                  <div
                    key={`${group.id}-${index}`}
                    data-testid={`safer-use-checklist-item-${group.id}-${index}`}
                    className="flex items-start gap-3 rounded-2xl border border-white/5 bg-white/5 px-4 py-3"
                  >
                    <span className="mt-0.5 h-5 w-5 rounded-full border border-[rgb(var(--primary))]/50 flex-shrink-0" />
                    <p className="text-sm text-[rgb(var(--text-secondary))] leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </article>
          ))}
        </section>

        <section className="mt-8 rounded-[2rem] border border-[rgb(var(--primary))]/20 bg-gradient-to-r from-[rgb(var(--primary))]/10 to-[rgb(var(--secondary))]/10 p-5" data-testid="safer-use-checklist-footer-card">
          <h3 className="text-lg font-semibold text-[rgb(var(--text-primary))] mb-2">Need more support right now?</h3>
          <p className="text-sm text-[rgb(var(--text-secondary))] leading-relaxed mb-4">
            You can open Nad for a quick check-in, or head to Aftercare & Grounding if your body needs a softer landing.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => navigate('/chat?action=need_a_pause')}
              data-testid="safer-use-checklist-chat-button"
              className="rounded-full party-gradient text-white"
            >
              Talk to Nad
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/post-party-care')}
              data-testid="safer-use-checklist-aftercare-button"
              className="rounded-full border-[rgb(var(--primary))]/40 text-[rgb(var(--primary))] hover:bg-[rgb(var(--primary))]/10"
            >
              Open Aftercare & Grounding
            </Button>
          </div>
        </section>
      </main>
    </div>
  );
}